/**
 * Small pieces the AI assistant screens share: the assistants' letter marks, the
 * UnfoldWIN ↔ assistant lockup, pill buttons, the 40×24 switch and segmented control
 * from the designs, and the purple "with Claude" chip.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { ASSISTANTS, type AssistantId } from "@workspace/assistants";
import { cn } from "@/lib/utils";

/** Design tokens from the Figma screens, kept together so every assistant surface matches. */
export const INK = "text-[#2f2925]";
export const MUTED = "text-[#76695f]";
export const BODY = "text-[#4a413b]";
export const PURPLE = "text-[#6e53ac]";
export const HAIRLINE = "border-[rgba(47,41,37,0.08)]";

/**
 * The Claude/ChatGPT connector isn't live yet: every entry point shows "Coming soon" and
 * nothing can be connected or handed off. Flip to false at launch — the flows are intact.
 */
export const ASSISTANTS_COMING_SOON = true;

const MARK_BG: Record<AssistantId, string> = { claude: "bg-[#fbe7dc]", chatgpt: "bg-[#e2efe4]" };

const MARK_SIZE = {
  44: "size-11 rounded-[13px] text-[18px]",
  40: "size-10 rounded-xl text-[17px]",
  30: "size-[30px] rounded-[9px] text-[13px]",
  26: "size-[26px] rounded-lg text-[11px]",
  22: "size-[22px] rounded-[7px] text-[9px]",
} as const;

export function AssistantMark({ assistant, size = 40, className }: { assistant: AssistantId; size?: keyof typeof MARK_SIZE; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn("flex shrink-0 items-center justify-center font-semibold", INK, MARK_BG[assistant], MARK_SIZE[size], className)}
    >
      {ASSISTANTS[assistant].mark}
    </span>
  );
}

export function UnfoldwinMark({ size = 40, className }: { size?: number; className?: string }) {
  return (
    <img
      src="/images/assistants/unfoldwin-mark.png"
      alt=""
      width={size}
      height={size}
      className={cn("shrink-0 rounded-full", className)}
      style={{ width: size, height: size }}
    />
  );
}

/** The two marks joined by three dots. `assistantFirst` on the consent page (the assistant is asking). */
export function ConnectMarks({ assistant, assistantFirst = false, size = 40 }: { assistant: AssistantId; assistantFirst?: boolean; size?: 40 | 44 }) {
  const parts = [
    <UnfoldwinMark key="u" size={size} />,
    <span key="d" className="flex items-center gap-[10px]" aria-hidden>
      {[0, 1, 2].map((i) => (
        <span key={i} className="size-1 rounded-full bg-[#c9bcf5]" />
      ))}
    </span>,
    <AssistantMark key="a" assistant={assistant} size={size} />,
  ];
  return <div className="flex items-center gap-[10px]">{assistantFirst ? parts.reverse() : parts}</div>;
}

type PillVariant = "dark" | "outline" | "purple" | "ghost";

const PILL: Record<PillVariant, string> = {
  dark: "bg-[#2f2925] text-white hover:bg-[#2f2925]/90",
  outline: `border border-[rgba(47,41,37,0.14)] bg-white ${INK} hover:bg-[#f8f6f2]`,
  purple: "bg-[#6e53ac] text-white hover:bg-[#6e53ac]/90",
  ghost: `${PURPLE} hover:bg-[rgba(110,83,172,0.08)]`,
};

export const PillButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: PillVariant; size?: "sm" | "md" }>(
  function PillButton({ variant = "dark", size = "md", className, type = "button", ...props }, ref) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[22px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6e53ac]/40 disabled:pointer-events-none disabled:opacity-50",
          size === "md" ? "px-5 py-[11px] text-sm" : "px-5 py-2 text-[13px]",
          PILL[variant],
          className,
        )}
        {...props}
      />
    );
  },
);

/** The designs' 40×24 switch. `locked` is the greyed "Required" state: on, and not changeable. */
export function AccessSwitch({
  checked,
  onCheckedChange,
  locked = false,
  disabled = false,
  label,
}: {
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  locked?: boolean;
  disabled?: boolean;
  label: string;
}) {
  return (
    <SwitchPrimitives.Root
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={locked || disabled}
      aria-label={label}
      className={cn(
        "relative inline-flex h-6 w-10 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6e53ac]/40 disabled:cursor-not-allowed",
        "data-[state=checked]:bg-[#6e53ac] data-[state=unchecked]:bg-[rgba(47,41,37,0.12)]",
        locked && "data-[state=checked]:bg-[#6e53ac]/45",
        disabled && !locked && "opacity-50",
      )}
    >
      <SwitchPrimitives.Thumb className="pointer-events-none block size-5 translate-x-0.5 rounded-full bg-white shadow-[0_1px_2px_rgba(47,41,37,0.2)] transition-transform data-[state=checked]:translate-x-[18px]" />
    </SwitchPrimitives.Root>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex shrink-0 gap-1 rounded-[14px] bg-[rgba(47,41,37,0.05)] p-1">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={cn(
              "rounded-[10px] px-4 py-2 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6e53ac]/40",
              on ? `bg-white font-semibold ${INK} shadow-[0_1px_1.5px_rgba(47,41,37,0.12)]` : `font-medium ${MUTED} hover:text-[#2f2925]`,
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Small rounded label: "Required", "Off by default", "Just turned on". */
export function Tag({ tone = "neutral", children }: { tone?: "neutral" | "purple" | "amber"; children: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center rounded-[14px] px-[7px] py-[2px] text-[10px] font-medium leading-normal",
        tone === "neutral" && `bg-[rgba(47,41,37,0.05)] ${MUTED}`,
        tone === "purple" && `bg-[rgba(110,83,172,0.1)] ${PURPLE}`,
        tone === "amber" && "border border-[rgba(230,197,148,0.8)] bg-[#fcf2d6] text-[#7a5212]",
      )}
    >
      {children}
    </span>
  );
}

/** The purple chip on Momentum steps: "Planned with Claude", "Wed, Oct 14 · 8:00 AM". */
export const AssistantChip = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { icon: ReactNode; compact?: boolean }>(
  function AssistantChip({ icon, compact = false, className, children, type = "button", ...props }, ref) {
    const interactive = Boolean(props.onClick) || "aria-haspopup" in props || "aria-expanded" in props;
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "inline-flex max-w-full items-center gap-1.5 rounded-full bg-[rgba(110,83,172,0.1)] font-medium text-[#6e53ac] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6e53ac]/40",
          compact ? "px-2 py-[3px] text-[11px]" : "px-2.5 py-1 text-xs",
          interactive ? "transition-colors hover:bg-[rgba(110,83,172,0.16)]" : "cursor-default",
          className,
        )}
        {...props}
      >
        <span className="shrink-0 [&>svg]:size-3">{icon}</span>
        <span className="truncate">{children}</span>
      </button>
    );
  },
);
